import React, { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { formatDate, getDayName, getMonthName } from "../utils/timeUtils";

export default function Calendar({
  selectedDate,
  onSelectDate,
  closedDays = [],
  maxAdvanceDays = 10,
}) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const parsedMaxAdvanceDays = Number.isFinite(Number(maxAdvanceDays)) && Number(maxAdvanceDays) > 0
    ? Number(maxAdvanceDays)
    : 10;
  const maxDate = new Date(today);
  maxDate.setDate(today.getDate() + parsedMaxAdvanceDays);
  maxDate.setHours(0, 0, 0, 0);

  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());

  const calendarDays = useMemo(() => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const startPad = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;

    const days = [];

    for (let i = startPad - 1; i >= 0; i--) {
      const d = new Date(currentYear, currentMonth, -i);
      days.push({ date: d, isCurrentMonth: false });
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      const d = new Date(currentYear, currentMonth, i);
      days.push({ date: d, isCurrentMonth: true });
    }

    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(currentYear, currentMonth + 1, i);
      days.push({ date: d, isCurrentMonth: false });
    }

    return days;
  }, [currentMonth, currentYear]);

  const goToPrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  const isDisabled = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    if (d < today) return true;
    if (d > maxDate) return true;
    if (closedDays.includes(d.getDay())) return true;
    return false;
  };

  const isSelected = (date) => selectedDate === formatDate(date);
  const isToday = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.getTime() === today.getTime();
  };

  const dayHeaders = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

  return (
    <div className="calendar">
      {/* Month Navigation */}
      <div className="calendar-nav">
        <button onClick={goToPrevMonth} className="calendar-nav-btn">
          <ChevronLeft />
        </button>
        <h4 className="calendar-month">
          {getMonthName(currentMonth)} {currentYear}
        </h4>
        <button onClick={goToNextMonth} className="calendar-nav-btn">
          <ChevronRight />
        </button>
      </div>

      {/* Day Headers */}
      <div className="calendar-header">
        {dayHeaders.map((day) => (
          <div key={day} className="calendar-header-day">{day}</div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="calendar-grid">
        {calendarDays.map((dayObj, index) => {
          const disabled = isDisabled(dayObj.date) || !dayObj.isCurrentMonth;
          const selected = isSelected(dayObj.date);
          const todayMark = isToday(dayObj.date);

          let className = "calendar-day";
          if (!dayObj.isCurrentMonth) className += " other-month";
          if (selected) className += " selected";
          else if (todayMark) className += " today";

          return (
            <button
              key={index}
              disabled={disabled}
              onClick={() => onSelectDate(formatDate(dayObj.date))}
              className={className}
            >
              {dayObj.date.getDate()}
              {todayMark && !selected && <span className="calendar-today-dot" />}
            </button>
          );
        })}
      </div>

      {/* Selected Date Info */}
      {selectedDate && (
        <div className="calendar-selected-info">
          <p>
            Seçilen tarih:{" "}
            <strong>
              {new Date(selectedDate + "T00:00:00").getDate()}{" "}
              {getMonthName(new Date(selectedDate + "T00:00:00").getMonth())}{" "}
              {new Date(selectedDate + "T00:00:00").getFullYear()},{" "}
              {getDayName(new Date(selectedDate + "T00:00:00"))}
            </strong>
          </p>
        </div>
      )}
    </div>
  );
}
